/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import {
  Button,
  Confirm,
  Form,
  Grid,
  Input,
  DateInput,
} from '@mairistem/composants';

import { Exemption, type Individu, type ListeRecensement } from '@/types';
import {
  OPTIONS_EXEMPTION,
  OPTIONS_PERIODE_RECENSEMENT,
  OPTIONS_RECENSE,
} from '@/constants';
import { validationRecensement } from '@/utils';

import PanelModalEdition from '../PanelModalEdition';

interface RecensementModalProps {
  demandeExemption: Exemption;
  recensement: ListeRecensement;
  numeroRecensement: number;
  trimestreRecensement: number;
  dateRecensement: string;
  anneeRecensement: number;
  updated: boolean;
  onSave: () => Promise<void>;
  onChange: (data: Partial<Individu>) => void;
  onClose: () => void;
  onCancel: () => void;
}

const RecensementModal = (props: RecensementModalProps) => {
  const {
    demandeExemption,
    recensement,
    numeroRecensement,
    trimestreRecensement,
    dateRecensement,
    anneeRecensement,
    updated,
    onChange,
    onSave,
    onClose,
    onCancel,
  } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [openConfirmSave, setOpenConfirmSave] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Partial<Individu> & { api?: boolean }
  >({});
  const [exemptionActive, setExemptionActive] = React.useState<boolean>(
    demandeExemption !== Exemption.SansObjet,
  );

  const handleChange = React.useCallback(
    (
      _e: React.FormEvent<HTMLInputElement>,
      data: { name: string; value: unknown },
    ) => {
      onChange({
        [data.name]: data.value,
      });
    },
    [onChange],
  );

  const handleChangeDate = React.useCallback(
    (date: Date | null) => {
      onChange({
        dateRecensement: date ? dayjs(date).format('YYYY-MM-DD') : '',
      });
    },
    [onChange],
  );

  const handleChangeExemptionActive = React.useCallback(
    (_e: React.FormEvent<HTMLInputElement>, data: { checked: boolean }) => {
      setExemptionActive(data.checked);
      if (!data.checked) {
        onChange({
          demandeExemption: Exemption.SansObjet,
        });
      }
    },
    [onChange],
  );

  const handleChangeAnneeRecensement = React.useCallback(
    (_e: React.FormEvent<HTMLInputElement>, { value }: { value: string }) => {
      if (_.size(value) < 5) {
        onChange({
          anneeRecensement: value,
        });
      }
    },
    [onChange],
  );

  const handleConfirmSave = React.useCallback(() => {
    setOpenConfirmSave(false);
    setLoading(true);

    onSave()
      .then(() => {
        onClose();
      })
      .catch(() => {
        setErrors({ api: true });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [onClose, onSave]);

  const handleSave = React.useCallback(() => {
    const e = validationRecensement(
      {
        trimestreRecensement,
        anneeRecensement,
        dateRecensement,
        recensement,
        demandeExemption,
      },
      exemptionActive,
    );

    setErrors(e);

    if (_.isEmpty(e)) {
      if (updated) {
        setOpenConfirmSave(true);
      } else {
        handleConfirmSave();
      }
    }
  }, [
    anneeRecensement,
    dateRecensement,
    demandeExemption,
    exemptionActive,
    handleConfirmSave,
    recensement,
    trimestreRecensement,
    updated,
  ]);

  return (
    <>
      {openConfirmSave && (
        <Confirm
          open
          content={{
            content: (
              <span>
                Attention, êtes-vous bien sûr de vouloir modifier le trimestre
                de rattachement ?
                <br />
                Le retour en arrière ne sera plus possible.
              </span>
            ),
          }}
          cancelButton={
            <Button basic size="tiny" icon="cancel" content="Annuler" />
          }
          confirmButton={<Button positive icon="check" content="Confirmer" />}
          onCancel={() => setOpenConfirmSave(false)}
          onConfirm={handleConfirmSave}
        />
      )}
      <PanelModalEdition
        errors={errors}
        loading={loading}
        header="INFORMATIONS DE RECENSEMENT"
        updated={false}
        onSave={handleSave}
        onClose={onCancel}
      >
        <Grid as={Form}>
          <Grid.Row>
            <Grid.Column width={2}>
              <Form.Field>
                <Input
                  fluid
                  disabled
                  label="N° d'ordre"
                  value={
                    _.isNil(numeroRecensement) || numeroRecensement === 0
                      ? ''
                      : numeroRecensement
                  }
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column width={3}>
              <Form.Field
                {...(numeroRecensement !== 0 &&
                  !_.isNil(numeroRecensement) && {
                    disabled: true,
                  })}
              >
                <label>Recensé sur le</label>
                <Form.Dropdown
                  name="trimestreRecensement"
                  data-testid="fiche-panel-recensement-modal-edition-periode-recensement"
                  options={OPTIONS_PERIODE_RECENSEMENT}
                  placeholder={OPTIONS_PERIODE_RECENSEMENT[0].text}
                  value={trimestreRecensement}
                  onChange={handleChange}
                  error={errors?.trimestreRecensement}
                  compact
                  selection
                  scrolling
                  {...(numeroRecensement !== 0 &&
                    !_.isNil(numeroRecensement) && {
                      disabled: true,
                    })}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column width={2}>
              <Form.Input
                type="number"
                name="anneeRecensement"
                fluid
                label="de"
                value={anneeRecensement}
                onChange={handleChangeAnneeRecensement}
                error={!_.isEmpty(errors?.anneeRecensement)}
                input={{
                  'data-testid': 'fiche-panel-recensement-modal-edition-annee',
                }}
                {...(numeroRecensement !== 0 &&
                  !_.isNil(numeroRecensement) && {
                    disabled: true,
                    title:
                      "Impossible de modifier l'année de recensement si le recensé est déjà numéroté",
                  })}
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <Form.Field>
                <label>en date du</label>
                <DateInput
                  data-testid="fiche-panel-recensement-modal-edition-date-recensement"
                  value={new Date(dateRecensement)}
                  onChange={handleChangeDate}
                  error={!_.isEmpty(errors?.dateRecensement)}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column width={5}>
              <Form.Field>
                <label>Inscrit sur la liste</label>
                <Form.Dropdown
                  name="recensement"
                  data-testid="fiche-panel-recensement-modal-edition-liste"
                  options={OPTIONS_RECENSE}
                  value={recensement}
                  placeholder={OPTIONS_RECENSE[0].text}
                  onChange={handleChange}
                  compact
                  selection
                  fluid
                  scrolling
                  error={!_.isEmpty(errors?.recensement)}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column
              width={6}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <Form.Checkbox
                name="exemptionActive"
                data-testid="fiche-panel-recensement-modal-edition-checkbox-exemption"
                label="Demande exemption au motif de"
                checked={exemptionActive}
                onChange={handleChangeExemptionActive}
                // error={!_.isEmpty(error?.exemption)}
              />
            </Grid.Column>
            <Grid.Column width={10}>
              <Form.Field>
                <Form.Dropdown
                  name="demandeExemption"
                  placeholder="Sélectionner un motif d'exemption"
                  data-testid="fiche-panel-recensement-modal-edition-dropdown-exemption"
                  compact
                  fluid
                  selection
                  value={demandeExemption}
                  options={OPTIONS_EXEMPTION}
                  disabled={!exemptionActive}
                  onChange={handleChange}
                  error={!_.isEmpty(errors?.demandeExemption)}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </PanelModalEdition>
    </>
  );
};

export default RecensementModal;
